import React, {useRef, useState} from 'react'
import {IconButton, Popover, Tooltip, Typography} from '@mui/material'
import GetAppRoundedIcon from '@mui/icons-material/GetAppRounded'
import {useTranslation} from 'react-i18next'
import {toast} from 'sonner'
import {styled} from 'common'
import {exportMeterReadings} from 'services/externalApi/meterReadingsApi'
import {useGetConsumersQuery, useGetMetersQuery} from 'store/Store'
import {useUrlParams} from 'services/hooks'
import {isSameKey} from 'utils'
import Autocomplete from 'components/Control/Autocomplete'
import BasicDatePicker from 'components/Control/BasicDatePicker'
import Button from 'components/Control/Button'

const STATUS_OPTIONS = [
    {key: 'PENDING', name: 'PENDING'},
    {key: 'PAID', name: 'PAID'},
    {key: 'FAILED', name: 'FAILED'},
]

const ExportMeterReadingsAction = () => {
    const {t} = useTranslation()
    const anchorRef = useRef(null)
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const {urlParams} = useUrlParams()

    const {data: consumersData} = useGetConsumersQuery({limit: 500}, {skip: !open})
    const {data: metersData} = useGetMetersQuery({limit: 500}, {skip: !open})

    const consumers = (consumersData?.data || consumersData || []).map((c) => ({
        key: c._id,
        name: c.name,
    }))
    const meters = (metersData?.data || metersData || []).map((m) => ({
        key: m._id,
        name: m.name || m.meterId,
    }))

    const handleExport = () => {
        const params = {}
        if (urlParams.from_date) params.from_date = urlParams.from_date
        if (urlParams.to_date) params.to_date = urlParams.to_date
        if (urlParams.status) params.status = urlParams.status
        if (urlParams.consumer) params.consumer = urlParams.consumer
        if (urlParams.meter) params.meter = urlParams.meter

        setLoading(true)
        toast.promise(
            exportMeterReadings(params).finally(() => setLoading(false)),
            {
                loading: t('loading'),
                success: () => t('meter_readings.export.success'),
                error: () => t('meter_readings.export.error'),
            }
        )
    }

    return (
        <>
            <Tooltip title={t('meter_readings.export.title')}>
                <IconButton ref={anchorRef} onClick={() => setOpen(true)}>
                    <GetAppRoundedIcon />
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <FormContainer>
                    <Typography variant="subtitle2" fontWeight={600}>
                        {t('meter_readings.export.title')}
                    </Typography>

                    <BasicDatePicker label={t('filters.from_date')} name="from_date" />
                    <BasicDatePicker label={t('filters.to_date')} name="to_date" />

                    <Autocomplete
                        label={t('filters.status')}
                        labelKey="status"
                        options={STATUS_OPTIONS}
                        multiple
                        disableCloseOnSelect
                        isOptionEqualToValue={isSameKey}
                        sx={{width: '100%'}}
                    />

                    <Autocomplete
                        label={t('filters.consumer')}
                        labelKey="consumer"
                        options={consumers}
                        multiple
                        disableCloseOnSelect
                        isOptionEqualToValue={isSameKey}
                        sx={{width: '100%'}}
                    />

                    <Autocomplete
                        label={t('filters.meter')}
                        labelKey="meter"
                        options={meters}
                        multiple
                        disableCloseOnSelect
                        isOptionEqualToValue={isSameKey}
                        sx={{width: '100%'}}
                    />

                    <Button
                        variant="contained"
                        size="small"
                        disabled={loading}
                        startIcon={<GetAppRoundedIcon />}
                        onClick={handleExport}>
                        {t('meter_readings.export.button')}
                    </Button>
                </FormContainer>
            </Popover>
        </>
    )
}

export default ExportMeterReadingsAction

const FormContainer = styled.div`
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 280px;
`
